import React from 'react';
import { Routes, Route, useLocation, Link } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import ProductPage from './pages/ProductPage';
import PaymentPage from './pages/PaymentPage';
import AdminPanel from './pages/AdminPanel';
import AdminLogin from './pages/AdminLogin';
import ProtectedRoute from './components/ProtectedRoute';
import Feedbacks from './pages/Feedbacks';
import NotFound from './pages/NotFound';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import FAQ from './pages/FAQ';
import Catalog from './pages/Catalog';
import Rebrand from './pages/Rebrand';
import Reviews from './pages/Reviews';

function App() {
  const location = useLocation();
  const hideNavbarPaths = ['/product', '/payment', '/feedbacks'];
  const shouldShowNavbar = !hideNavbarPaths.some(path => location.pathname.startsWith(path));

  return (
    <div className="relative min-h-screen w-full bg-[#0a1a0a]">
      {/* Background Effects Container - Fixed to viewport */}
      <div className="fixed inset-0 w-full h-full overflow-hidden z-0 will-change-transform">
        {/* Stars Background */}
        <div className="absolute inset-0 bg-[#0a1a0a]">
          {/* Stars Layer 1 - Top */}
          <div className="relative w-full h-full will-change-transform">
            {[...Array(10)].map((_, i) => (
              <div key={`top-${i}`} className="star-top"></div>
            ))}
          </div>

          {/* Stars Layer 2 - Middle */}
          <div className="relative w-full h-full will-change-transform">
            {[...Array(10)].map((_, i) => (
              <div key={`middle-${i}`} className="star-middle"></div>
            ))}
          </div>

          {/* Stars Layer 3 - Bottom */}
          <div className="relative w-full h-full will-change-transform">
            {[...Array(10)].map((_, i) => (
              <div key={`bottom-${i}`} className="star-bottom"></div>
            ))}
          </div>

          {/* Subtle Glow Layer - Optimized */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(34,197,94,0.1),transparent_70%)] will-change-transform"></div>
        </div>
      </div>

      {/* Main Content Wrapper */}
      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Navigation */}
        {shouldShowNavbar && (
          <div className="w-full bg-transparent">
            <Navbar />
          </div>
        )}
        
        {/* Page Content */}
        <main className="flex-grow">
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
          />
          
          <Routes>
            <Route path="/" element={<Catalog />} />
            <Route path="/rebrand" element={<Rebrand />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/:customUrl" element={<ProductPage />} />
            <Route path="/payment/:invoiceId" element={<PaymentPage />} />
            <Route path="/feedbacks" element={<Feedbacks />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            {/* Catch-all route for invalid URLs */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        {/* Footer */}
        <footer className="relative mt-20 border-t border-[#22c55e]/10 z-20">
          {/* Glow Effect */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(34,197,94,0.05),transparent_50%)]"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(34,197,94,0.05)] to-transparent"></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 py-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* OLYMP Section */}
              <div>
                <Link to="/" className="inline-block">
                  <h3 className="text-2xl font-bold text-white mb-4 hover:text-[#22c55e] transition-colors duration-200">OLYMP</h3>
                </Link>
                <p className="text-gray-400 text-sm">
                  Premium Discord services with instant delivery and 24/7 support.
                </p>
              </div>

              {/* Support Section */}
              <div>
                <h4 className="text-lg font-bold text-white mb-4">Support</h4>
                <ul className="space-y-2">
                  <li>
                    <Link 
                      to="/terms" 
                      className="inline-block text-gray-400 hover:text-[#22c55e] hover:underline transition-colors duration-200"
                    >
                      Terms of Service
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/privacy" 
                      className="inline-block text-gray-400 hover:text-[#22c55e] hover:underline transition-colors duration-200"
                    >
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/faq" 
                      className="inline-block text-gray-400 hover:text-[#22c55e] hover:underline transition-colors duration-200"
                    >
                      FAQ
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Contact Section */}
              <div>
                <h4 className="text-lg font-bold text-white mb-4">Contact</h4>
                <ul className="space-y-2">
                  <li>
                    <a 
                      href="https://discord.gg/olymp4" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-block text-gray-400 hover:text-[#22c55e] hover:underline transition-colors duration-200"
                    >
                      Discord
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://t.me/olympstorecc" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-block text-gray-400 hover:text-[#22c55e] hover:underline transition-colors duration-200"
                    >
                      Telegram
                    </a>
                  </li>
                </ul>
              </div>

              {/* Reviews Section */}
              <div>
                <h4 className="text-lg font-bold text-white mb-4">Reviews</h4>
                <ul className="space-y-2">
                  <li>
                    <Link 
                      to="/reviews" 
                      className="inline-block text-gray-400 hover:text-[#22c55e] hover:underline transition-colors duration-200"
                    >
                      View All Reviews
                    </Link>
                  </li>
                  <li>
                    <a 
                      href="https://myvouch.es/olymp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-gray-400 hover:text-[#22c55e] hover:underline transition-colors duration-200"
                    >
                      Myvouch.es
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="relative mt-8 pt-8 border-t border-[#22c55e]/10">
              <p className="text-gray-400 text-sm text-center">
                © {new Date().getFullYear()} OLYMP. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App; 